import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, Users, Printer, Search } from 'lucide-react';
import PrintDeliveriesContent from './print/PrintDeliveriesContent';

function AdminPrintDeliveries({ userId, setActiveComponent }) {
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPrintView, setShowPrintView] = useState(false);

  useEffect(() => {
    fetchSellers();
  }, [userId]);

  async function fetchSellers() {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/provider/sellers`,
        { params: { provider_user_id: userId } }
      );
      setSellers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching sellers:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleSellerSelect(seller) {
    setSelectedSeller(seller);
    setShowPrintView(true);
  }

  function handlePrintAll() {
    setSelectedSeller({ id: 'all', username: 'Todos los vendedores' });
    setShowPrintView(true);
  }

  function handleBackToSelection() {
    setShowPrintView(false);
    setSelectedSeller(null);
  }

  const filteredSellers = sellers.filter(seller =>
    seller.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    seller.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (showPrintView) {
    return (
      <PrintDeliveriesContent
        userId={selectedSeller.id === 'all' ? userId : selectedSeller.id}
        isAdminView={true}
        isAllUsers={selectedSeller.id === 'all'}
        adminUserId={userId}
        sellerName={selectedSeller.username}
        onBackClick={handleBackToSelection}
      />
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('OptionsAdmin')}
              className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>
            <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat flex items-center gap-2">
              <Printer className="w-6 h-6" />
              Imprimir Entregas
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          
          {/* Instructions Card */}
          <div className="bg-gradient-to-r from-[#B1336E]/10 to-[#E6B43C]/10 border-l-4 border-[#B1336E] rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-[#B1336E] flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-base font-bold text-[#B1336E] font-montserrat mb-1">
                  Selecciona un vendedor
                </h2>
                <p className="text-sm text-slate-700 font-montserrat">
                  Elige un vendedor para imprimir sus entregas o selecciona "Todos" para imprimir todas las entregas
                </p>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar vendedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm"
              />
            </div>
          </div>

          {/* Print All Button */}
          <button
            onClick={handlePrintAll}
            className="w-full mb-4 bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white font-semibold py-4 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 font-montserrat flex items-center justify-center gap-3"
          >
            <Printer className="w-5 h-5" />
            <span>Imprimir Todas las Entregas</span>
          </button>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-[#B1336E] border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredSellers.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
                No se encontraron vendedores
              </h3>
              <p className="text-sm text-slate-600 font-montserrat">
                {searchTerm ? 'Intenta con otro término de búsqueda' : 'No hay vendedores vinculados a esta cuenta'}
              </p>
            </div>
          )}

          {/* Sellers List */}
          {!loading && filteredSellers.length > 0 && (
            <div className="space-y-3">
              {filteredSellers.map(seller => (
                <button
                  key={seller.id}
                  onClick={() => handleSellerSelect(seller)}
                  className="w-full bg-white rounded-xl border-2 border-slate-200 hover:border-[#B1336E] hover:shadow-md transition-all duration-200 p-4 text-left group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#B1336E] to-[#E6B43C] flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-white" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-slate-900 font-montserrat truncate group-hover:text-[#B1336E] transition-colors">
                        {seller.username || `Usuario ${seller.id}`}
                      </h3>
                      {seller.email && (
                        <p className="text-sm text-slate-600 font-montserrat truncate">
                          {seller.email}
                        </p>
                      )}
                    </div>

                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-[#B1336E] flex items-center justify-center transition-colors">
                        <Printer className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPrintDeliveries;